import { SupportPlatform, type SupportContextInput } from '__generated__/graphql';
// Import from sidenavLinks, not layoutLinks: this util must not load React permission hooks.
import { rawUserMainLinks } from 'layout/sidenavLinks';

export type SupportContextParams = Record<string, string | undefined>;

export const SUPPORT_CONTEXT_SCREEN = {
  TASK_ASSIGNMENT: 'Task assignment',
  TASK: 'Task',
  REPORT_RESPONSE: 'Report response',
  BROADCAST: 'Broadcast',
} as const;

const PROJECT_WORKSPACE_SEGMENT = /^\/projects\/[^/]+\/([^/]+)/;

const PROJECT_SIDEBAR_SCREEN_BY_SEGMENT = new Map(
  rawUserMainLinks.map((link) => [link.path.replace(/^\//, ''), link.title])
);

export type SupportContextClientInput = Pick<
  SupportContextInput,
  'campaignId' | 'platform' | 'route' | 'screen' | 'entityType' | 'entityId' | 'locale' | 'timezone'
>;

export function campaignIdFromParams(params: SupportContextParams): string | undefined {
  return params.projectId || params.campaignId || undefined;
}

export function screenFromPathname(pathname: string): string | undefined {
  const segment = pathname.match(PROJECT_WORKSPACE_SEGMENT)?.[1];
  if (!segment) {
    return undefined;
  }
  return PROJECT_SIDEBAR_SCREEN_BY_SEGMENT.get(segment);
}

export function buildSupportContextClientInput(args: {
  pathname: string;
  params: SupportContextParams;
  platform?: SupportPlatform;
  locale?: string;
  timezone?: string;
}): SupportContextClientInput {
  const entity: { entityType?: string; entityId?: string; screen?: string } = args.params
    .assignmentId
    ? {
        entityType: 'assignment',
        entityId: args.params.assignmentId,
        screen: SUPPORT_CONTEXT_SCREEN.TASK_ASSIGNMENT,
      }
    : args.params.activityId
      ? {
          entityType: 'task',
          entityId: args.params.activityId,
          screen: SUPPORT_CONTEXT_SCREEN.TASK,
        }
      : args.params.templateResponseId
        ? {
            entityType: 'report',
            entityId: args.params.templateResponseId,
            screen: SUPPORT_CONTEXT_SCREEN.REPORT_RESPONSE,
          }
        : args.params.broadcastId
          ? {
              entityType: 'broadcast',
              entityId: args.params.broadcastId,
              screen: SUPPORT_CONTEXT_SCREEN.BROADCAST,
            }
          : {};
  const screen = entity.screen ?? screenFromPathname(args.pathname);
  return {
    ...entity,
    ...(screen != null ? { screen } : {}),
    campaignId: campaignIdFromParams(args.params),
    platform: args.platform ?? SupportPlatform.Web,
    route: args.pathname,
    locale: args.locale,
    timezone: args.timezone,
  };
}
